import { type PageUpdatePreviewRow } from '@elementary/common-ui/components/AiChat';
import { WarehouseType } from '@elementary/common-ui/types';
import { Path, PathValue, UseFormReturn } from 'react-hook-form';
import {
  AssetSourceFormData,
  BigQueryAuthMethod,
  EnvironmentForm,
  WarehouseDbFilterFormData,
} from 'shared/types';
import {
  BIGQUERY_SERVICE_ACCOUNT_DOCS_URL,
  BIGQUERY_WIF_DOCS_URL,
  CONNECTION_STEP_INDEX,
  CONNECTION_STEP_TITLES,
  CONTENT_STEP_INDEX,
  DBT_PACKAGE_DOCS_URL,
  ELEMENTARY_IP,
  ENVIRONMENT_CONTENT_STEP_TITLE,
  ENVIRONMENT_DRAWER_ACTIONS,
  MAX_PORT,
  MIN_PORT,
} from '../constants';
import {
  getAssetSourceRules,
  getAthenaAuthMethod,
  getBigQueryAuthMethod,
  getDatabricksAuthMethod,
  getDefaultWarehouseAssetSource,
  getSnowflakeAuthMethod,
  isValidPort,
  readErrorMessage,
} from '../utils';
import {
  ASSET_SOURCE_PREVIEW_LABEL,
  ASSET_SOURCES_FIELD,
  CONNECTION_STEP_DESCRIPTION,
  CONTENT_STEP_DESCRIPTION,
  ENVIRONMENT_DETAILS_FIELDS,
  MAX_PREVIEW_VALUE_LENGTH,
  SSH_TUNNEL_NOTE,
  UNKNOWN_FIELD_PREVIEW,
  USER_ONLY_FIELD_PREVIEW,
  WAREHOUSE_FIELDS,
  WAREHOUSE_TYPE_FIELD,
} from './constants';
import {
  EnvironmentFormFieldSpec,
  EnvironmentFormSecretStatus,
  EnvironmentFormStep,
  EnvironmentFormWriteResult,
} from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const clip = (value: string) =>
  value.length > MAX_PREVIEW_VALUE_LENGTH
    ? `${value.slice(0, MAX_PREVIEW_VALUE_LENGTH).trimEnd()}…`
    : value;

export const getEnvironmentFormSteps = (
  isEditMode: boolean,
): EnvironmentFormStep[] => [
  {
    index: CONNECTION_STEP_INDEX,
    name: isEditMode
      ? CONNECTION_STEP_TITLES.edit
      : CONNECTION_STEP_TITLES.create,
    description: CONNECTION_STEP_DESCRIPTION,
  },
  {
    index: CONTENT_STEP_INDEX,
    name: ENVIRONMENT_CONTENT_STEP_TITLE,
    description: CONTENT_STEP_DESCRIPTION,
  },
];

export const isWarehouseType = (value: unknown): value is WarehouseType =>
  typeof value === 'string' && value in WAREHOUSE_FIELDS;

export const resolveEnvironmentFormValues = (
  values: Record<string, unknown>,
): Record<string, unknown> => ({
  ...values,
  snowflakeAuthMethod: getSnowflakeAuthMethod(values),
  athenaAuthMethod: getAthenaAuthMethod(values),
  bigqueryAuthMethod: getBigQueryAuthMethod(values),
  databricksAuthMethod: getDatabricksAuthMethod(values),
  databricksStorageAccessType: values.databricksStorageAccessType ?? null,
});

const ALL_ENVIRONMENT_FIELDS: EnvironmentFormFieldSpec[] = [
  ...ENVIRONMENT_DETAILS_FIELDS,
  WAREHOUSE_TYPE_FIELD,
  ...Object.values(WAREHOUSE_FIELDS).flat(),
  ASSET_SOURCES_FIELD,
];

export const findEnvironmentFieldSpec = (
  name: string,
): EnvironmentFormFieldSpec | undefined =>
  ALL_ENVIRONMENT_FIELDS.find((spec) => spec.name === name);

export const isUserOnlyField = (spec: EnvironmentFormFieldSpec) =>
  spec.kind === 'secret' || spec.kind === 'file';

export const getEnvironmentStepFields = (
  step: number,
  values: Record<string, unknown>,
): EnvironmentFormFieldSpec[] => {
  if (step === CONTENT_STEP_INDEX) {
    return [ASSET_SOURCES_FIELD];
  }
  const resolved = resolveEnvironmentFormValues(values);
  const warehouseFields = isWarehouseType(resolved.warehouseType)
    ? WAREHOUSE_FIELDS[resolved.warehouseType]
    : [];
  return [
    ...ENVIRONMENT_DETAILS_FIELDS,
    WAREHOUSE_TYPE_FIELD,
    ...warehouseFields.filter(
      ({ visibleWhen }) =>
        !visibleWhen ||
        visibleWhen.values.includes(resolved[visibleWhen.field]),
    ),
  ];
};

export const getUserOnlyFieldStatus = (
  spec: EnvironmentFormFieldSpec,
  value: unknown,
): EnvironmentFormSecretStatus => {
  if (value === true) {
    return 'saved';
  }
  if (spec.kind === 'file') {
    if (isRecord(value) && typeof value.content === 'string') {
      return 'entered';
    }
    return isRecord(value) && typeof value.name === 'string' && value.name
      ? 'saved'
      : 'empty';
  }
  return typeof value === 'string' && value !== '' ? 'entered' : 'empty';
};

const isEmptyValue = (value: unknown) =>
  value == null ||
  (typeof value === 'string' && value.trim() === '') ||
  (typeof value === 'number' && Number.isNaN(value));

const isSavedAssetSource = (
  source: AssetSourceFormData,
  existingAssetSourceIds: string[],
) => source.id != null && existingAssetSourceIds.includes(source.id);

const getMissingAssetSourceFields = (
  sources: AssetSourceFormData[],
  warehouseType: WarehouseType,
  existingAssetSourceIds: string[],
): string[] => {
  if (sources.length === 0) {
    return ['assetSources'];
  }
  const rules = getAssetSourceRules(warehouseType);
  return sources.flatMap((source, index) => {
    const prefix = `assetSources.${index}`;
    const missing: string[] = [];
    const isNameRequired =
      source.type === 'dbt' ||
      !isSavedAssetSource(source, existingAssetSourceIds);
    if (isNameRequired && isEmptyValue(source.name)) {
      missing.push(`${prefix}.name`);
    }
    if (source.type === 'dbt') {
      if (rules.isDbtDatabaseRequired && isEmptyValue(source.database)) {
        missing.push(`${prefix}.database`);
      }
      if (isEmptyValue(source.schema)) {
        missing.push(`${prefix}.schema`);
      }
      return missing;
    }
    source.dbFilters.forEach((filter, filterIndex) => {
      if (isEmptyValue(filter.database)) {
        missing.push(`${prefix}.dbFilters.${filterIndex}.database`);
      }
    });
    return missing;
  });
};

export const getMissingRequiredFields = (
  step: number,
  values: EnvironmentForm,
  existingAssetSourceIds: string[],
): string[] => {
  if (step === CONTENT_STEP_INDEX) {
    return isWarehouseType(values.warehouseType)
      ? getMissingAssetSourceFields(
          values.assetSources ?? [],
          values.warehouseType,
          existingAssetSourceIds,
        )
      : [];
  }
  const resolved = resolveEnvironmentFormValues(values);
  return getEnvironmentStepFields(step, values)
    .filter((spec) => spec.required)
    .filter((spec) =>
      isUserOnlyField(spec)
        ? getUserOnlyFieldStatus(spec, resolved[spec.name]) === 'empty'
        : isEmptyValue(resolved[spec.name]),
    )
    .map((spec) => spec.name);
};

export const describeAssetSources = (
  sources: AssetSourceFormData[],
  existingAssetSourceIds: string[],
) =>
  sources.map((source) => {
    const isExisting = isSavedAssetSource(source, existingAssetSourceIds);
    const lockedFields =
      source.type === 'dbt' ? ['type', 'database', 'schema'] : ['type'];
    return {
      ...source,
      id: source.id ?? null,
      isExisting,
      lockedFields: isExisting ? lockedFields : [],
    };
  });

export const flattenFormErrors = (
  errors: unknown,
  path = '',
): Record<string, string> => {
  const message = readErrorMessage(errors);
  if (message !== undefined) {
    return path ? { [path]: message } : {};
  }
  if (!errors || typeof errors !== 'object') {
    return {};
  }
  return Object.entries(errors).reduce<Record<string, string>>(
    (flattened, [key, value]) =>
      key === 'ref'
        ? flattened
        : {
            ...flattened,
            ...flattenFormErrors(value, path ? `${path}.${key}` : key),
          },
    {},
  );
};

export const parseEnvironmentFormWrite = (
  args: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  const raw =
    'form' in args
      ? args.form
      : Object.fromEntries(
          Object.entries(args).filter(([key]) => key !== 'reason'),
        );
  const parsed = parseJsonIfString(raw);
  return isRecord(parsed) ? parsed : undefined;
};

export const normalizeEnvironmentFieldWrite = (
  spec: EnvironmentFormFieldSpec,
  value: unknown,
): EnvironmentFormWriteResult => {
  switch (spec.kind) {
    case 'text':
      if (typeof value === 'string') {
        return { ok: true, value };
      }
      if (typeof value === 'number') {
        return { ok: true, value: String(value) };
      }
      return { ok: false, reason: 'Must be a string' };
    case 'select': {
      const isNone = value === null || value === '' || value === 'none';
      if (isNone && spec.allowsNone) {
        return { ok: true, value: undefined };
      }
      if (spec.options?.some((option) => option.value === value)) {
        return { ok: true, value };
      }
      const allowed = (spec.options ?? [])
        .map((option) => option.value)
        .join(', ');
      return {
        ok: false,
        reason: spec.allowsNone
          ? `Must be one of: ${allowed}, or null for none`
          : `Must be one of: ${allowed}`,
      };
    }
    case 'boolean':
      if (typeof value === 'boolean') {
        return { ok: true, value };
      }
      if (value === 'true' || value === 'false') {
        return { ok: true, value: value === 'true' };
      }
      return { ok: false, reason: 'Must be true or false' };
    case 'port': {
      const port =
        typeof value === 'string' && value.trim() !== ''
          ? Number(value)
          : value;
      if (isValidPort(port)) {
        return { ok: true, value: spec.storesNumber ? port : String(port) };
      }
      return {
        ok: false,
        reason: `Must be an integer between ${MIN_PORT} and ${MAX_PORT}`,
      };
    }
    default:
      return { ok: false, reason: 'Not writable' };
  }
};

const readOptionalString = (
  item: Record<string, unknown>,
  key: string,
): EnvironmentFormWriteResult<string | undefined> => {
  const value = item[key];
  if (value === undefined || value === null) {
    return { ok: true, value: undefined };
  }
  if (typeof value === 'string') {
    return { ok: true, value };
  }
  if (typeof value === 'number') {
    return { ok: true, value: String(value) };
  }
  return { ok: false, reason: `${key} must be a string` };
};

const normalizeSchemas = (
  value: unknown,
): EnvironmentFormWriteResult<string[]> => {
  const parsed = parseJsonIfString(value);
  if (parsed === undefined || parsed === null) {
    return { ok: true, value: [] };
  }
  const list = typeof parsed === 'string' ? parsed.split(',') : parsed;
  if (
    !Array.isArray(list) ||
    !list.every((schema) => typeof schema === 'string')
  ) {
    return { ok: false, reason: 'schemas must be a list of strings' };
  }
  return {
    ok: true,
    value: list.map((schema) => schema.trim()).filter((schema) => !!schema),
  };
};

const normalizeDbFilters = (
  value: unknown,
  hasWarehouseSchemas: boolean,
  warehouseType: WarehouseType,
): EnvironmentFormWriteResult<WarehouseDbFilterFormData[]> => {
  if (!Array.isArray(value) || value.length === 0) {
    return {
      ok: false,
      reason: 'dbFilters must be a non-empty list of { database, schemas }',
    };
  }
  const filters: WarehouseDbFilterFormData[] = [];
  for (const [index, filter] of value.entries()) {
    if (!isRecord(filter)) {
      return { ok: false, reason: `dbFilters[${index}] must be an object` };
    }
    const database = readOptionalString(filter, 'database');
    if (!database.ok) {
      return { ok: false, reason: `dbFilters[${index}].${database.reason}` };
    }
    const schemas = normalizeSchemas(filter.schemas);
    if (!schemas.ok) {
      return { ok: false, reason: `dbFilters[${index}].${schemas.reason}` };
    }
    if (!hasWarehouseSchemas && schemas.value.length > 0) {
      return {
        ok: false,
        reason: `${warehouseType} warehouse sources filter by database only; omit schemas`,
      };
    }
    filters.push({ database: database.value ?? '', schemas: schemas.value });
  }
  return { ok: true, value: filters };
};

const normalizeAssetSourceWrite = (
  item: unknown,
  current: AssetSourceFormData | undefined,
  isExisting: boolean,
  warehouseType: WarehouseType,
): EnvironmentFormWriteResult<AssetSourceFormData> => {
  if (!isRecord(item)) {
    return { ok: false, reason: 'Must be an object' };
  }
  const type = item.type ?? current?.type;
  if (type !== 'dbt' && type !== 'warehouse') {
    return { ok: false, reason: 'type must be "dbt" or "warehouse"' };
  }
  if (isExisting && current && type !== current.type) {
    return {
      ok: false,
      reason: `Saved ${current.type} sources cannot change type`,
    };
  }
  const name = readOptionalString(item, 'name');
  if (!name.ok) {
    return name;
  }
  const idProp = current?.id ? { id: current.id } : {};
  const rules = getAssetSourceRules(warehouseType);

  if (type === 'dbt') {
    const currentDbt = current?.type === 'dbt' ? current : undefined;
    const database = readOptionalString(item, 'database');
    const schema = readOptionalString(item, 'schema');
    if (!database.ok) {
      return database;
    }
    if (!schema.ok) {
      return schema;
    }
    if (!rules.hasDatabase && database.value) {
      return {
        ok: false,
        reason: `${warehouseType} dbt sources have no database; omit database`,
      };
    }
    if (
      isExisting &&
      currentDbt &&
      ((database.value !== undefined &&
        database.value !== currentDbt.database) ||
        (schema.value !== undefined && schema.value !== currentDbt.schema))
    ) {
      return {
        ok: false,
        reason: 'database and schema of a saved dbt source cannot change',
      };
    }
    return {
      ok: true,
      value: {
        ...idProp,
        type: 'dbt',
        name: name.value ?? currentDbt?.name ?? '',
        database: rules.hasDatabase
          ? database.value ?? currentDbt?.database ?? ''
          : undefined,
        schema: schema.value ?? currentDbt?.schema ?? '',
      },
    };
  }

  const currentWarehouse = current?.type === 'warehouse' ? current : undefined;
  const dbFilters = normalizeDbFilters(
    parseJsonIfString(item.dbFilters) ??
      currentWarehouse?.dbFilters ??
      getDefaultWarehouseAssetSource().dbFilters,
    rules.hasWarehouseSchemas,
    warehouseType,
  );
  if (!dbFilters.ok) {
    return dbFilters;
  }
  return {
    ok: true,
    value: {
      ...idProp,
      type: 'warehouse',
      name: name.value ?? currentWarehouse?.name ?? '',
      dbFilters: dbFilters.value,
    },
  };
};

export const normalizeAssetSourcesWrite = (
  value: unknown,
  {
    currentSources,
    existingAssetSourceIds,
    warehouseType,
  }: {
    currentSources: AssetSourceFormData[];
    existingAssetSourceIds: string[];
    warehouseType: WarehouseType;
  },
): EnvironmentFormWriteResult<AssetSourceFormData[]> => {
  const parsed = parseJsonIfString(value);
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return {
      ok: false,
      reason: 'Must be a non-empty list of asset sources',
    };
  }
  const seenIds = new Set<string>();
  const sources: AssetSourceFormData[] = [];
  for (const [index, item] of parsed.entries()) {
    const id = isRecord(item) && typeof item.id === 'string' ? item.id : null;
    const current = id
      ? currentSources.find((source) => source.id === id)
      : undefined;
    if (id && !current) {
      return {
        ok: false,
        reason: `assetSources[${index}].id "${id}" is not in the form; omit id to add a new source`,
      };
    }
    if (id && seenIds.has(id)) {
      return {
        ok: false,
        reason: `assetSources[${index}].id "${id}" appears more than once`,
      };
    }
    if (id) {
      seenIds.add(id);
    }
    const normalized = normalizeAssetSourceWrite(
      item,
      current,
      !!id && existingAssetSourceIds.includes(id),
      warehouseType,
    );
    if (!normalized.ok) {
      return {
        ok: false,
        reason: `assetSources[${index}]: ${normalized.reason}`,
      };
    }
    sources.push(normalized.value);
  }
  return { ok: true, value: sources };
};

export const setEnvironmentFormField = (
  methods: UseFormReturn<EnvironmentForm>,
  field: Path<EnvironmentForm>,
  value: unknown,
  { shouldDirty }: { shouldDirty: boolean },
) => {
  methods.setValue(
    field,
    value as PathValue<EnvironmentForm, Path<EnvironmentForm>>,
    { shouldDirty, shouldValidate: true },
  );
};

const formatAssetSource = (source: unknown): string => {
  if (!isRecord(source)) {
    return JSON.stringify(source);
  }
  const name =
    typeof source.name === 'string' && source.name
      ? source.name
      : 'Untitled source';
  if (source.type === 'dbt') {
    const location = [source.database, source.schema]
      .filter((part) => typeof part === 'string' && part)
      .join('.');
    return location ? `${name} (dbt, ${location})` : `${name} (dbt)`;
  }
  const filters = Array.isArray(source.dbFilters) ? source.dbFilters : [];
  const locations = filters.filter(isRecord).map((filter) => {
    const schemas = Array.isArray(filter.schemas) ? filter.schemas : [];
    const database =
      typeof filter.database === 'string' && filter.database
        ? filter.database
        : 'no database';
    return schemas.length > 0
      ? `${database} [${schemas.join(', ')}]`
      : `${database} [all]`;
  });
  return locations.length > 0
    ? `${name} (warehouse, ${locations.join('; ')})`
    : `${name} (warehouse)`;
};

const formatEnvironmentFieldValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return 'None';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return clip(String(value));
  }
  return clip(JSON.stringify(value));
};

const previewAssetSources = (value: unknown): PageUpdatePreviewRow[] => {
  const parsed = parseJsonIfString(value);
  if (!Array.isArray(parsed)) {
    return [
      {
        field: ASSET_SOURCES_FIELD.name,
        label: ASSET_SOURCES_FIELD.label,
        value: clip(String(value)),
      },
    ];
  }
  return parsed.map((source, index) => ({
    field: `${ASSET_SOURCES_FIELD.name}.${index}`,
    label: `${ASSET_SOURCE_PREVIEW_LABEL} ${index + 1}`,
    value: clip(formatAssetSource(source)),
  }));
};

export const previewEnvironmentFormUpdate = (
  proposed: Record<string, unknown>,
): PageUpdatePreviewRow[] =>
  Object.entries(proposed).flatMap(([field, value]) => {
    const spec = findEnvironmentFieldSpec(field);
    if (!spec) {
      return [{ field, label: field, value: UNKNOWN_FIELD_PREVIEW }];
    }
    if (isUserOnlyField(spec)) {
      return [{ field, label: spec.label, value: USER_ONLY_FIELD_PREVIEW }];
    }
    if (spec.kind === 'assetSources') {
      return previewAssetSources(value);
    }
    return [
      { field, label: spec.label, value: formatEnvironmentFieldValue(value) },
    ];
  });

export const getEnvironmentFormHint = ({
  isSaving,
  isEditMode,
  stepIndex,
}: {
  isSaving: boolean;
  isEditMode: boolean;
  stepIndex: number;
}) => {
  if (isSaving) {
    return 'The environment is being saved.';
  }
  const {
    continue: continueLabel,
    saveAndSync,
    saveChanges,
    configureAssetSources,
  } = ENVIRONMENT_DRAWER_ACTIONS;
  if (isEditMode) {
    return stepIndex === CONNECTION_STEP_INDEX
      ? `The user saves with "${saveChanges}" in the drawer header. "${configureAssetSources}" opens the ${ENVIRONMENT_CONTENT_STEP_TITLE} step.`
      : `The user saves with "${saveChanges}" in the drawer header.`;
  }
  return stepIndex === CONNECTION_STEP_INDEX
    ? `When the required fields are filled, the user clicks "${continueLabel}" to move to the ${ENVIRONMENT_CONTENT_STEP_TITLE} step.`
    : `The user clicks "${saveAndSync}" to create the environment. Elementary then connects to the warehouse; if that fails, submitError explains why.`;
};

export const getEnvironmentFormNotes = (
  stepIndex: number,
  resolvedValues: Record<string, unknown>,
) => {
  if (stepIndex === CONTENT_STEP_INDEX) {
    return [
      `dbt project sources require the Elementary dbt package to be installed and run: ${DBT_PACKAGE_DOCS_URL}`,
    ];
  }
  const notes = [
    `If the warehouse restricts access with an IP allowlist, Elementary's IP ${ELEMENTARY_IP} must be allowed.`,
  ];
  if (resolvedValues.sshEnabled === true) {
    notes.push(SSH_TUNNEL_NOTE);
  }
  if (resolvedValues.warehouseType === 'bigquery') {
    notes.push(
      resolvedValues.bigqueryAuthMethod === BigQueryAuthMethod.WIF
        ? `Workload Identity Federation setup: ${BIGQUERY_WIF_DOCS_URL}`
        : `Service account setup: ${BIGQUERY_SERVICE_ACCOUNT_DOCS_URL}`,
    );
  }
  return notes;
};
